<template>
    <div v-if="sortedNotificationMessages.length" class="notification-messages" :class="cssClasses">
        <component
            :is="isMobile && shouldNotificationDialogBeAvailable ? VBottomSheet : VDialog"
            v-if="!disabled"
            v-model="showNotificationDialog"
            v-bind="componentProps"
        >
            <notification-dialog-content
                :model-value="sortedNotificationMessages"
                v-bind="notificationDialogContentProps"
                @click-close="showNotificationDialog = false"
            />
        </component>

        <div class="notification-messages__container">
            <template v-if="loading">
                <div class="d-flex align-center">
                    <v-progress-circular color="primary" size="18" width="2" indeterminate class="mr-1" />
                    <div v-if="typeof loading === 'string'" class="notification-messages__text mx-2">
                        {{ loading }}
                    </div>
                </div>
            </template>
            <template v-else-if="compact">
                <div class="d-flex align-start notification-icon--overlap">
                    <notification-icon
                        v-for="message in sortedNotificationMessages.toReversed()"
                        :key="message.id"
                        :model-value="message.type"
                        size="24"
                        class="notification-messages__icon"
                    />
                    <div
                        class="notification-messages__text text-grey-darken-2 text-truncate"
                        v-html="getNotificationText(sortedNotificationMessages[0], hideNotificationBody)"
                    />
                </div>
            </template>
            <template v-else>
                <div
                    v-for="message in sortedNotificationMessages"
                    :key="message.id"
                    class="notification-messages__list-item d-flex align-start"
                >
                    <notification-icon :model-value="message.type" size="24" class="notification-messages__icon" />
                    <div
                        class="notification-messages__text text-grey-darken-2"
                        v-html="getNotificationText(message, hideNotificationBody)"
                    />
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { NotificationMessageDto } from '@/types/webapi';
import { useNotificationMessage } from './useNotificationMessage';
import NotificationDialogContent from '@/features/common/components/NotificationDialogContent.vue';
import { computed, ref } from 'vue';
import { useDisplay } from 'vuetify';
import { VDialog, VBottomSheet } from 'vuetify/components';
import { NotificationIcon } from '..';
import { sortBySeverity } from '../../helpers';

interface Props {
    messages?: NotificationMessageDto[];
    compact?: boolean;
    disabled?: boolean;
    loading?: boolean | string;
    hideNotificationBody?: boolean;
}

const componentProps = computed(() =>
    isMobile.value
        ? {
              activator: 'parent',
              class: 'notification-dialog--mobile'
          }
        : {
              activator: 'parent',
              width: '450'
          }
);
const notificationDialogContentProps = computed(() => (isMobile.value ? { rounded: 't-lg b-0' } : { rounded: 'lg' }));

const { mobile: isMobile } = useDisplay();
const props = withDefaults(defineProps<Props>(), { messages: () => [] });

const { getNotificationText } = useNotificationMessage();
const shouldNotificationDialogBeAvailable = computed(() => props.messages !== undefined && props.messages?.length > 0);
const showNotificationDialog = ref(false);
const cssClasses = computed(() => ({
    'chip-cursor': props.compact,
    'notification-messages--compact': props.compact
}));
const sortedNotificationMessages = computed(() => [...props.messages].toSorted(sortBySeverity));
</script>

<style lang="scss">
@use 'vuetify/settings' as *;

.notification-messages {
    &__icon + &__text {
        margin-left: $spacer;
    }

    &__text {
        font-size: calc(16px / $font-size-root) * 1rem;
        font-weight: 400;
        line-height: 1.25;
        padding-top: 2px;

        :not(.notification-messages--compact) & {
            text-wrap: auto;
        }
    }

    .notification-messages__container {
        position: relative;
        overflow: hidden;
    }

    .chip-cursor {
        cursor: pointer;
    }
}
</style>
